import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, ChevronLeft, ChevronRight, Package, Zap, TrendingUp, AlertTriangle, Hand, ImageIcon, Trash2 } from 'lucide-react';
import { useApp } from '../context/appContextCore';
import { IS_TENANT } from '../tenant/config';
import DriverAvatar, { fetchDriverPhotos } from '../components/DriverPhoto';

const APPS_URL = '/.netlify/functions/appsscript';
const ONBOARD_URL = '/.netlify/functions/onboard-sb';
// Day totals come from sheet1-utils-sb (parcel_events). Hollypaul reaches it through the
// appsscript proxy; a tenant build has no proxy and no driver portal, so it calls the
// function directly and shows Day Totals only (Onboard + Coverage need portal photos).
const TOTALS_URL = IS_TENANT ? '/.netlify/functions/sheet1-utils-sb' : APPS_URL;

// Map team leader team name → driver route area code
const TEAM_TO_AREA = { City: 'CITY', Darlinghurst: 'DARL', Broadway: 'BWAY' };
const OB_AREA_ORDER = ['DARL', 'CITY', 'BWAY'];
const OB_AREA_LABEL = { DARL: 'Darlinghurst', CITY: 'City', BWAY: 'Broadway', UNASSIGNED: 'Unassigned' };
const obTime = (iso) => iso ? new Date(iso).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' }) : '—';
const obEparcel = (s) => (s.eparcelInitial == null && s.eparcelSubsequent == null) ? null : (s.eparcelInitial || 0) + (s.eparcelSubsequent || 0);

function isoToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function addDays(iso, n) {
  const [y,m,d] = iso.split('-').map(Number);
  const dt = new Date(y, m-1, d+n);
  return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
}
function fmtDate(iso) {
  const [y,m,d] = iso.split('-').map(Number);
  return new Date(y,m-1,d).toLocaleDateString('en-AU',{ weekday:'short', day:'numeric', month:'short', year:'numeric' });
}
function toISO(dt) {
  return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
}
// Calendar Mon–Sun week containing `iso`.
function weekRange(iso) {
  const [y,m,d] = iso.split('-').map(Number);
  const dow = (new Date(y,m-1,d).getDay() + 6) % 7; // Mon=0 … Sun=6
  return [toISO(new Date(y,m-1,d-dow)), toISO(new Date(y,m-1,d-dow+6))];
}
// Calendar month containing `iso`.
function monthRange(iso) {
  const [y,m] = iso.split('-').map(Number);
  return [toISO(new Date(y,m-1,1)), toISO(new Date(y,m,0))];
}
function fmtRange(from, to) {
  if (from === to) return fmtDate(from);
  const opt = { day:'numeric', month:'short' };
  const [fy,fm,fd] = from.split('-').map(Number);
  const [ty,tm,td] = to.split('-').map(Number);
  const f = new Date(fy,fm-1,fd).toLocaleDateString('en-AU', opt);
  const t = new Date(ty,tm-1,td).toLocaleDateString('en-AU', { ...opt, year:'numeric' });
  return `${f} – ${t}`;
}

export default function DailySnapshotPage() {
  const { currentUser } = useApp();
  const isViewer = currentUser?.role === 'viewer';
  const viewerArea = isViewer ? TEAM_TO_AREA[currentUser?.team] : null;

  const today = isoToday();
  const [date, setDate]         = useState(today);
  const [tab, setTab]           = useState('totals'); // 'totals' | 'onboard' | 'coverage'
  const [rows, setRows]         = useState([]);
  const [photos, setPhotos]     = useState({});
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [lastLoaded, setLastLoaded] = useState(null);
  const [areaLogins, setAreaLogins] = useState(null); // set of logins for viewer's area
  const [obScans, setObScans]   = useState(null);
  const [obLoading, setObLoading] = useState(false);
  const [obError, setObError]   = useState('');
  const [obBusyId, setObBusyId] = useState('');
  const [lcRows, setLcRows]     = useState(null); // getLoginCheck rows — who worked (baseline for "no image")
  const [covScope, setCovScope] = useState('day'); // 'day' | 'week' | 'month'
  const [covDriver, setCovDriver] = useState('');  // driver-login filter (contains match)
  const [pvLoading, setPvLoading] = useState(false);
  const [pvErr, setPvErr]       = useState('');
  const [pvByDate, setPvByDate] = useState(null);  // getSheet1Totals byDate for the range
  const [pvScans, setPvScans]   = useState(null);  // onboardRange scans for the range

  // For viewers: load driver routes to know which logins belong to their area
  useEffect(() => {
    if (!viewerArea) return;
    fetch('/.netlify/functions/driver-routes-sb', { method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'loadDriverRoutes' }) })
      .then(r => r.json())
      .then(d => {
        if (d.routes) {
          const logins = new Set(
            d.routes.filter(r => r.area === viewerArea).map(r => r.driverLogin.toUpperCase())
          );
          setAreaLogins(logins);
        }
      })
      .catch(() => {});
  }, [viewerArea]);

  const load = useCallback(async (d) => {
    setLoading(true); setError('');
    try {
      const res = await fetch(TOTALS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'getSheet1Totals', periodStart: d, periodEnd: d }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'No data');
      const byDriver = data.byDate?.[d] || {};
      const list = Object.entries(byDriver)
        .map(([login, v]) => ({ login, parcel: Math.round(v.parcel||0), express: Math.round(v.express||0), onRoad: Math.round(v.onRoad||0), total: Math.round(v.total||0) }))
        .filter(r => r.total > 0)
        .sort((a,b) => b.total - a.total);
      setRows(list);
      setLastLoaded(new Date().toLocaleTimeString('en-AU',{ hour:'2-digit', minute:'2-digit' }));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(date); }, [date, load]);

  useEffect(() => {
    const logins = [...new Set((rows || []).map(x => (x.login || '').toUpperCase()).filter(Boolean))];
    if (logins.length && !IS_TENANT) fetchDriverPhotos(logins).then(p => setPhotos(prev => ({ ...p, ...prev })));
  }, [rows]);

  // Onboard tally — the drivers' scanned device SUMMARY screens (onboard_scans),
  // same data as the portal Tallies tab. Loaded lazily when the tab is opened.
  const loadOnboard = useCallback(async (d) => {
    setObLoading(true); setObError('');
    try {
      const res = await fetch(ONBOARD_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'onboardTeam', date: d }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'No data');
      setObScans(data.scans || []);
      setLastLoaded(new Date().toLocaleTimeString('en-AU',{ hour:'2-digit', minute:'2-digit' }));
    } catch (e) {
      setObError(e.message); setObScans([]);
    } finally {
      setObLoading(false);
    }
  }, []);

  useEffect(() => { if (tab === 'onboard' || tab === 'coverage') loadOnboard(date); }, [tab, date, loadOnboard]);

  // Coverage tab also needs the login-check baseline — who actually worked that
  // day — so a driver with a real load but no scan at all can be flagged (not
  // just those whose scan under-counted). Independent of the other fetches.
  useEffect(() => {
    if (tab !== 'coverage') return;
    let alive = true;
    fetch(APPS_URL, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'getLoginCheck', date }),
    })
      .then(r => r.json())
      .then(d => { if (alive) setLcRows(d && d.success ? (d.rows || []) : []); })
      .catch(() => { if (alive) setLcRows([]); });
    return () => { alive = false; };
  }, [tab, date]);

  // Period (week/month) reconciliation: one Day-Totals range pull + one onboard
  // range pull. Both backends already accept a date range, so no per-day looping.
  useEffect(() => {
    if (tab !== 'coverage' || covScope === 'day') return;
    const [from, to] = covScope === 'week' ? weekRange(date) : monthRange(date);
    let alive = true;
    setPvLoading(true); setPvErr(''); setPvByDate(null); setPvScans(null);
    Promise.all([
      fetch(APPS_URL, { method:'POST', headers:{ 'Content-Type':'application/json' },
        body: JSON.stringify({ action:'getSheet1Totals', periodStart: from, periodEnd: to }) }).then(r => r.json()),
      fetch(ONBOARD_URL, { method:'POST', headers:{ 'Content-Type':'application/json' },
        body: JSON.stringify({ action:'onboardRange', from, to }) }).then(r => r.json()),
    ])
      .then(([s1, ob]) => {
        if (!alive) return;
        if (!s1.success) throw new Error(s1.error || 'No day-totals data');
        setPvByDate(s1.byDate || {});
        setPvScans(ob && ob.success ? (ob.scans || []) : []);
        setLastLoaded(new Date().toLocaleTimeString('en-AU',{ hour:'2-digit', minute:'2-digit' }));
      })
      .catch(e => { if (alive) { setPvErr(e.message); setPvByDate({}); setPvScans([]); } })
      .finally(() => { if (alive) setPvLoading(false); });
    return () => { alive = false; };
  }, [tab, covScope, date]);

  async function obReocr(id) {
    setObBusyId(id);
    try {
      const res = await fetch(ONBOARD_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'onboardReocr', id }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Re-read failed');
      await loadOnboard(date);
    } catch (e) { window.alert(e.message); } finally { setObBusyId(''); }
  }

  async function obRemove(id, driver) {
    if (!window.confirm('Delete ' + driver + "'s duplicate scan? The photo goes too.")) return;
    setObBusyId(id);
    try {
      const res = await fetch(ONBOARD_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'onboardDelete', id }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Delete failed');
      await loadOnboard(date);
    } catch (e) { window.alert(e.message); } finally { setObBusyId(''); }
  }

  // For viewers: show only drivers from their area
  const visibleRows = isViewer && areaLogins
    ? rows.filter(r => areaLogins.has(r.login.toUpperCase()))
    : rows;

  const totals = visibleRows.reduce((acc, r) => ({
    parcel:  acc.parcel  + r.parcel,
    express: acc.express + r.express,
    onRoad:  acc.onRoad  + r.onRoad,
    total:   acc.total   + r.total,
  }), { parcel: 0, express: 0, onRoad: 0, total: 0 });

  // Onboard tab: each capture is a separate acceptance batch — a big depot load
  // first, then smaller top-ups later in the shift — NOT a retake. Headline per
  // driver = the LARGEST capture (drives area totals); other captures nest under
  // it. Viewers see only their area, matching the totals tab's scoping.
  const obByDriver = {};
  (obScans || []).forEach(s => { (obByDriver[s.driver] = obByDriver[s.driver] || []).push(s); });
  const obMainOf = (arr) => arr.slice().sort((a, b) => {
    const ai = a.items == null ? -1 : a.items, bi = b.items == null ? -1 : b.items;
    return bi !== ai ? bi - ai : (a.scannedAt < b.scannedAt ? -1 : 1);
  })[0];
  const obMain = {};    // driver -> largest capture (the headline)
  const obExtras = {};  // driver -> other captures, chronological
  Object.keys(obByDriver).forEach(d => {
    const m = obMainOf(obByDriver[d]);
    obMain[d] = m;
    obExtras[d] = obByDriver[d].filter(s => s.id !== m.id).sort((a, b) => (a.scannedAt < b.scannedAt ? -1 : 1));
  });
  const obDrivers = Object.values(obMain)
    .filter(s => !viewerArea || (s.area || 'UNASSIGNED') === viewerArea);

  // Likely DUPLICATE (not a genuine later top-up): a second photo of the SAME
  // load, taken because the driver wasn't sure the first one saved — shot within
  // a few minutes of the main capture, or reading the same #Items. A real top-up
  // load comes back much later with a different, smaller count.
  const OB_DUP_WINDOW_MIN = 10;
  const obMinsApart = (a, b) => {
    const ta = Date.parse(a), tb = Date.parse(b);
    return (isNaN(ta) || isNaN(tb)) ? Infinity : Math.abs(ta - tb) / 60000;
  };
  const obIsDupe = (e, m) => !!m && (obMinsApart(e.scannedAt, m.scannedAt) <= OB_DUP_WINDOW_MIN
    || (e.items != null && m.items != null && e.items === m.items));
  const obDupeList = [];
  obDrivers.forEach(m => {
    (obExtras[m.driver] || []).forEach(e => { if (obIsDupe(e, m)) obDupeList.push({ driver: m.driver, main: m, extra: e }); });
  });
  const obDupeIds = new Set(obDupeList.map(x => x.extra.id));
  const obAreas = viewerArea ? [viewerArea]
    : [...OB_AREA_ORDER, ...[...new Set(obDrivers.map(s => s.area || 'UNASSIGNED'))].filter(a => !OB_AREA_ORDER.includes(a))];
  const obSum = (list, key) => list.reduce((a, s) => a + (key === 'eparcel' ? (obEparcel(s) || 0) : (s[key] || 0)), 0);

  // ── Coverage reconciliation ────────────────────────────────────────────────
  // Why is the onboard tally so far below Day Totals? Two legitimate reasons and
  // one accuracy problem:
  //   • On Road ("O ROAD" signoffs) and Express (late-arrive EP) land on the AP
  //     device AFTER the driver photographs the depot summary — they CAN'T be in
  //     the onboard image. Expected onboard = Day Total − On Road − Express.
  //   • The residual (expected onboard that has no matching image) is the real
  //     accuracy gap: drivers who never scanned, scanned the wrong device, only
  //     scanned a top-up, or whose read failed.
  // Onboard here = the SUM of ALL that driver's captures (every acceptance batch),
  // not just the largest — the most generous credit, so the residual is a
  // conservative floor on the true no-image volume.
  const obItemsByDriver = {};   // login -> summed onboard items across all captures
  const obFlagsByDriver = {};   // login -> { captures, hasItems, ocrError, checksumBad, wrongDevice, area, latestUrl }
  (obScans || []).forEach(s => {
    const k = (s.driver || '').toUpperCase();
    if (!k) return;
    obItemsByDriver[k] = (obItemsByDriver[k] || 0) + (s.items || 0);
    const f = obFlagsByDriver[k] || { captures: 0, hasItems: false, ocrError: false, checksumBad: false, wrongDevice: false, area: null, viewUrl: null };
    f.captures += 1;
    if (s.items != null) f.hasItems = true;
    if (s.ocrError) f.ocrError = true;
    if (s.checksumOk === false) f.checksumBad = true;
    if (s.deviceLogin && s.deviceLogin !== s.driver) f.wrongDevice = true;
    if (!f.area && s.area) f.area = s.area;
    if (!f.viewUrl && s.viewUrl) f.viewUrl = s.viewUrl;
    obFlagsByDriver[k] = f;
  });

  // login -> area, from whatever source knows it (onboard scan or login-check row)
  const areaByLogin = {};
  (obScans || []).forEach(s => { const k = (s.driver || '').toUpperCase(); if (k && s.area && !areaByLogin[k]) areaByLogin[k] = s.area; });
  (lcRows || []).forEach(r => { const k = (r.login || '').toUpperCase(); if (k && r.area && !areaByLogin[k]) areaByLogin[k] = (r.area || '').toUpperCase(); });

  // Per-driver coverage rows, built from everyone with real Day-Total volume.
  const covRows = visibleRows.map(r => {
    const login    = r.login.toUpperCase();
    const expected = Math.max(0, r.total - r.onRoad - r.express); // depot load that SHOULD have an image
    const onboard  = obItemsByDriver[login] || 0;
    const f        = obFlagsByDriver[login];
    const gap      = expected - onboard;
    let why = 'OK';
    if (expected <= 0)               why = 'NONE_EXPECTED'; // all volume was on-road / express
    else if (!f)                     why = 'NO_IMAGE';
    else if (!f.hasItems && f.ocrError) why = 'OCR_FAILED';
    else if (f.wrongDevice)          why = 'WRONG_DEVICE';
    else if (f.checksumBad)          why = 'CHECKSUM';
    else if (expected >= 25 && onboard < expected * 0.7) why = 'UNDER_CAPTURED';
    return { login, expected, onboard, gap, why, area: areaByLogin[login] || 'UNASSIGNED', captures: f ? f.captures : 0, viewUrl: f ? f.viewUrl : null };
  });

  const covTotals = covRows.reduce((a, r) => ({
    expected: a.expected + r.expected,
    onboard:  a.onboard  + r.onboard,
  }), { expected: 0, onboard: 0 });
  const covDayTotal = visibleRows.reduce((a, r) => a + r.total, 0);
  const covOnRoad   = visibleRows.reduce((a, r) => a + r.onRoad, 0);
  const covExpress  = visibleRows.reduce((a, r) => a + r.express, 0);
  const covResidual = covTotals.expected - covTotals.onboard;

  // The attention list — anything that isn't cleanly covered, worst gap first.
  const WHY_ORDER = ['NO_IMAGE', 'UNDER_CAPTURED', 'OCR_FAILED', 'WRONG_DEVICE', 'CHECKSUM'];
  const covQ = covDriver.trim().toUpperCase();
  const covFlagged = covRows
    .filter(r => WHY_ORDER.includes(r.why))
    .filter(r => !covQ || r.login.includes(covQ))
    .sort((a, b) => b.gap - a.gap);
  const covClean = covRows.filter(r => r.why === 'OK').length;
  const WHY_META = {
    NO_IMAGE:       { label: 'No image',        color: '#ef4444', note: 'Worked with a real load but never scanned a summary' },
    UNDER_CAPTURED: { label: 'Under-captured',  color: '#f59e0b', note: 'Scan is well below the depot load — likely only a top-up' },
    OCR_FAILED:     { label: 'Read failed',     color: '#f59e0b', note: 'Photo stored but OCR could not read it — re-read on the Onboard tab' },
    WRONG_DEVICE:   { label: 'Wrong device',    color: '#f59e0b', note: 'Scanned a device signed on to another driver' },
    CHECKSUM:       { label: 'Checksum off',    color: '#f59e0b', note: "Lines don't add up to #Items — check the photo" },
  };

  // ── Period (week/month) aggregation ────────────────────────────────────────
  // Same identity as the daily view, summed across the range per driver. Onboard
  // is matched per driver PER DAY (a scan only covers its own day), so a driver
  // who scans Mon–Thu but misses Fri shows one missed day and Friday's gap.
  const pvViewerScoped = (login) => !isViewer || !areaLogins || areaLogins.has(login.toUpperCase());
  const [pvFrom, pvTo] = covScope === 'week' ? weekRange(date) : covScope === 'month' ? monthRange(date) : [date, date];
  const obDayDriver = {}; // `${scanDate}|${LOGIN}` -> summed items (all captures that day)
  const pvAreaByLogin = {};
  (pvScans || []).forEach(s => {
    const lg = (s.driver || '').toUpperCase(); if (!lg) return;
    obDayDriver[`${s.scanDate}|${lg}`] = (obDayDriver[`${s.scanDate}|${lg}`] || 0) + (s.items || 0);
    if (s.area && !pvAreaByLogin[lg]) pvAreaByLogin[lg] = s.area;
  });
  const pvAgg = {}; // LOGIN -> aggregate across the range
  Object.entries(pvByDate || {}).forEach(([dt, drivers]) => {
    Object.entries(drivers).forEach(([login, v]) => {
      const lg = login.toUpperCase();
      const expected = Math.max(0, (v.total || 0) - (v.onRoad || 0) - (v.express || 0));
      const onb = obDayDriver[`${dt}|${lg}`] || 0;
      const a = pvAgg[lg] || (pvAgg[lg] = { login: lg, expected: 0, onboard: 0, onRoad: 0, express: 0, daysWorked: 0, daysImage: 0, daysMissing: 0 });
      a.expected += expected; a.onboard += onb;
      a.onRoad += (v.onRoad || 0); a.express += (v.express || 0);
      if (expected > 0) { a.daysWorked += 1; if (onb > 0) a.daysImage += 1; else a.daysMissing += 1; }
    });
  });
  const pvAll = Object.values(pvAgg).filter(a => pvViewerScoped(a.login)).map(a => ({
    ...a, gap: a.expected - a.onboard, area: pvAreaByLogin[a.login] || 'UNASSIGNED',
  }));
  const pvTotals = pvAll.reduce((t, a) => ({
    expected: t.expected + a.expected, onboard: t.onboard + a.onboard,
    onRoad: t.onRoad + a.onRoad, express: t.express + a.express,
  }), { expected: 0, onboard: 0, onRoad: 0, express: 0 });
  const pvDayTotal = pvTotals.expected + pvTotals.onRoad + pvTotals.express;
  const pvResidual = pvTotals.expected - pvTotals.onboard;
  // Repeat offenders: worked days with no image, then biggest gap. Driver filter applied.
  const pvFlagged = pvAll
    .filter(a => a.daysMissing > 0 || (a.expected >= 25 && a.onboard < a.expected * 0.7))
    .filter(a => !covQ || a.login.includes(covQ))
    .sort((x, y) => (y.daysMissing - x.daysMissing) || (y.gap - x.gap));
  const pvClean = pvAll.length - pvFlagged.length;

  const isToday = date === today;
  const isFuture = date > today;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:0, maxWidth:600, margin:'0 auto' }}>

      {/* ── Header ── */}
      <div style={{ padding:'16px 16px 0', display:'flex', flexDirection:'column', gap:10 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <div style={{ fontSize:'1.3rem', fontWeight:800, color:'var(--hp-gold)' }}>Daily Snapshot</div>
            <div style={{ fontSize:'0.75rem', color:'var(--hp-dim)', marginTop:2 }}>
              {lastLoaded ? `Updated ${lastLoaded}` : 'Parcels & Express by driver'}
            </div>
          </div>
          <button onClick={() => (tab === 'onboard' ? loadOnboard(date) : load(date))} disabled={loading || obLoading} style={{ background:'var(--hp-surface)', border:'1px solid var(--hp-border)', borderRadius:'var(--radius)', padding:'8px', cursor:'pointer', color:'var(--hp-dim)', display:'flex', alignItems:'center' }}>
            <RefreshCw size={16} style={(loading || obLoading) ? { animation:'spin 1s linear infinite' } : {}} />
          </button>
        </div>

        {/* Date navigation */}
        <div style={{ display:'flex', alignItems:'center', gap:8, background:'var(--hp-card)', border:'1px solid var(--hp-border)', borderRadius:'var(--radius-lg)', padding:'10px 14px' }}>
          <button onClick={() => setDate(d => addDays(d,-1))} style={{ background:'none', border:'none', color:'var(--hp-dim)', cursor:'pointer', padding:4, borderRadius:6, display:'flex', alignItems:'center' }}>
            <ChevronLeft size={20} />
          </button>
          <div style={{ flex:1, textAlign:'center' }}>
            <div style={{ fontWeight:700, fontSize:'0.95rem', color:'var(--hp-text)' }}>{fmtDate(date)}</div>
            {isToday && <div style={{ fontSize:'0.68rem', color:'var(--hp-gold)', fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase' }}>Today</div>}
          </div>
          <button onClick={() => setDate(d => addDays(d,1))} disabled={isFuture} style={{ background:'none', border:'none', color: isFuture ? 'var(--hp-muted)' : 'var(--hp-dim)', cursor: isFuture ? 'default' : 'pointer', padding:4, borderRadius:6, display:'flex', alignItems:'center' }}>
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Tab switcher */}
        {!IS_TENANT && <div style={{ display:'flex', gap:8 }}>
          {[
            { key:'totals',   label:'Day Totals' },
            { key:'onboard',  label:'Onboard' },
            { key:'coverage', label:'Coverage' },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              flex:1, padding:'9px 0', borderRadius:'var(--radius)', cursor:'pointer',
              fontWeight:800, fontSize:'0.78rem', letterSpacing:'0.06em', textTransform:'uppercase',
              background: tab === t.key ? 'var(--hp-gold)' : 'var(--hp-card)',
              color:      tab === t.key ? 'var(--hp-black)' : 'var(--hp-dim)',
              border: `1px solid ${tab === t.key ? 'var(--hp-gold)' : 'var(--hp-border)'}`,
            }}>
              {t.label}
            </button>
          ))}
        </div>}

        {/* Totals bar */}
        {tab === 'totals' && visibleRows.length > 0 && (
          <div className="stack-mobile" style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:8 }}>
            {[
              { icon: Package, label:'Parcels',  value: totals.parcel,  color:'var(--hp-text)' },
              { icon: Zap,     label:'Express',  value: totals.express, color:'#f59e0b' },
              { icon: AlertTriangle, label:'On Road', value: totals.onRoad, color:'#6366f1' },
              { icon: TrendingUp, label:'Total', value: totals.total,   color:'var(--hp-gold)' },
            ].map(({ icon: Icon, label, value, color }) => (
              <div key={label} style={{ background:'var(--hp-card)', border:'1px solid var(--hp-border)', borderRadius:'var(--radius)', padding:'10px 8px', textAlign:'center' }}>
                <div style={{ fontSize:'1.5rem', fontWeight:800, color, lineHeight:1 }}>{value}</div>
                <div style={{ fontSize:'0.65rem', color:'var(--hp-dim)', textTransform:'uppercase', letterSpacing:'0.08em', marginTop:3, display:'flex', alignItems:'center', justifyContent:'center', gap:3 }}>
                  <Icon size={10} />{label}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Onboard totals bar */}
        {tab === 'onboard' && obDrivers.length > 0 && (
          <div className="stack-mobile" style={{ display:'grid', gridTemplateColumns:'repeat(2, 1fr)', gap:8 }}>
            {[
              { icon: Package, label:'Onboard',  value: obSum(obDrivers, 'items'),        color:'var(--hp-gold)' },
              { icon: Hand,    label:'Held',     value: obSum(obDrivers, 'heldArticles'), color:'#6366f1' },
            ].map(({ icon: Icon, label, value, color }) => (
              <div key={label} style={{ background:'var(--hp-card)', border:'1px solid var(--hp-border)', borderRadius:'var(--radius)', padding:'10px 8px', textAlign:'center' }}>
                <div style={{ fontSize:'1.5rem', fontWeight:800, color, lineHeight:1 }}>{value}</div>
                <div style={{ fontSize:'0.65rem', color:'var(--hp-dim)', textTransform:'uppercase', letterSpacing:'0.08em', marginTop:3, display:'flex', alignItems:'center', justifyContent:'center', gap:3 }}>
                  <Icon size={10} />{label}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Onboard tally (scanned device summaries) ── */}
      {tab === 'onboard' && (
        <div style={{ padding:'12px 16px', display:'flex', flexDirection:'column', gap:10 }}>

          {obLoading && (
            <div style={{ textAlign:'center', padding:'40px 0', color:'var(--hp-dim)', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
              <RefreshCw size={16} style={{ animation:'spin 1s linear infinite' }} /> Loading…
            </div>
          )}

          {obError && (
            <div style={{ background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:'var(--radius)', padding:'12px 16px', color:'#ef4444', fontSize:'0.875rem' }}>
              {obError}
            </div>
          )}

          {!obLoading && !obError && obDrivers.length === 0 && (
            <div style={{ textAlign:'center', padding:'40px 0', color:'var(--hp-dim)', fontSize:'0.9rem' }}>
              No onboard summaries scanned this day yet.
            </div>
          )}

          {/* Possible duplicate photos — same load shot twice (unsure the first
              saved). Compare, then remove the extra; the tally already ignores it. */}
          {!obLoading && obDupeList.length > 0 && (
            <div style={{ background:'var(--hp-card)', border:'1px solid #f59e0b55', borderRadius:'var(--radius)', padding:'12px 14px' }}>
              <div style={{ fontWeight:800, fontSize:'0.9rem', color:'#f59e0b', marginBottom:4 }}>
                Possible duplicate photo{obDupeList.length === 1 ? '' : 's'} ({obDupeList.length})
              </div>
              <div style={{ fontSize:'0.68rem', color:'var(--hp-dim)', marginBottom:6 }}>
                Same load photographed twice within minutes. Compare the two, then remove the duplicate — the headline tally already keeps only the main capture.
              </div>
              {obDupeList.map(({ driver: dd, main: m, extra: e }) => (
                <div key={e.id} style={{ display:'flex', alignItems:'center', gap:8, fontSize:'0.8rem', borderTop:'1px solid var(--hp-border)', padding:'7px 0' }}>
                  <span style={{ flex:1, minWidth:0 }}>
                    <span style={{ fontFamily:'var(--font-mono)', fontWeight:700, color:'var(--hp-text)' }}>{dd}</span>
                    <span style={{ color:'var(--hp-dim)', fontSize:'0.72rem' }}>
                      {' '}kept {obTime(m.scannedAt)} ({m.items ?? '—'}) · duplicate {obTime(e.scannedAt)} ({e.ocrError ? 'read failed' : (e.items ?? '—')})
                    </span>
                  </span>
                  {m.viewUrl && <a href={m.viewUrl} target="_blank" rel="noreferrer" title="View kept photo" style={{ color:'var(--hp-dim)', display:'inline-flex' }}><ImageIcon size={14} /></a>}
                  {e.viewUrl && <a href={e.viewUrl} target="_blank" rel="noreferrer" title="View duplicate" style={{ color:'var(--hp-dim)', display:'inline-flex' }}><ImageIcon size={14} /></a>}
                  {!isViewer && (
                    <button onClick={() => obRemove(e.id, dd)} disabled={obBusyId === e.id} title="Remove the duplicate photo"
                      style={{ background:'none', border:'1px solid #f59e0b', borderRadius:6, color:'#f59e0b', fontSize:'0.68rem', fontWeight:700, padding:'2px 10px', cursor:'pointer' }}>
                      {obBusyId === e.id ? '…' : 'Remove'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {!obLoading && obAreas.map(a => {
            const list = obDrivers.filter(s => (s.area || 'UNASSIGNED') === a).sort((x, y) => (x.scannedAt < y.scannedAt ? -1 : 1));
            if (!list.length) return null;
            return (
              <div key={a} style={{ background:'var(--hp-card)', border:'1px solid var(--hp-border)', borderRadius:'var(--radius)', padding:'12px 14px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:8 }}>
                  <div style={{ fontWeight:800, fontSize:'0.9rem', color:'var(--hp-text)' }}>{OB_AREA_LABEL[a] || a}</div>
                  <div style={{ fontSize:'0.75rem', color:'var(--hp-dim)' }}>
                    <strong style={{ color:'var(--hp-gold)', fontSize:'0.95rem' }}>{obSum(list, 'items')}</strong> onboard · {list.length} driver{list.length === 1 ? '' : 's'}
                  </div>
                </div>

                {/* Column headers */}
                <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:'0.6rem', color:'var(--hp-dim)', textTransform:'uppercase', letterSpacing:'0.05em', padding:'2px 0 4px' }}>
                  <span style={{ flex:1 }}>Driver</span>
                  <span style={{ width:34, textAlign:'right' }}>Held</span>
                  <span style={{ width:44, textAlign:'right' }}>Items</span>
                  <span style={{ width:20 }} />
                </div>

                {list.map(s => (
                  <div key={s.id} style={{ borderTop:'1px solid var(--hp-border)', padding:'8px 0' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:'0.82rem' }}>
                      <span style={{ flex:1, minWidth:0 }}>
                        <span style={{ fontFamily:'var(--font-mono)', fontWeight:700, color:'var(--hp-text)' }}>{s.driver}</span>
                        <span style={{ color:'var(--hp-dim)', fontSize:'0.7rem' }}> {obTime(s.scannedAt)}{obExtras[s.driver] && obExtras[s.driver].length ? ' · main load' : ''}</span>
                      </span>
                      <span style={{ width:34, textAlign:'right', color: s.heldArticles > 0 ? '#6366f1' : 'var(--hp-dim)', fontWeight:700 }}>{s.heldArticles ?? '—'}</span>
                      <span style={{ width:44, textAlign:'right', color:'var(--hp-gold)', fontWeight:800, fontSize:'0.95rem' }}>{s.items ?? '—'}</span>
                      <span style={{ width:20, textAlign:'right' }}>
                        {s.viewUrl && (
                          <a href={s.viewUrl} target="_blank" rel="noreferrer" style={{ color:'var(--hp-dim)', display:'inline-flex' }} title="View the scanned photo">
                            <ImageIcon size={14} />
                          </a>
                        )}
                      </span>
                    </div>
                    {/* Other captures this day — later top-ups, nested under the main load. */}
                    {obExtras[s.driver] && obExtras[s.driver].length > 0 && obExtras[s.driver].map(e => (
                      <div key={e.id} style={{ display:'flex', alignItems:'center', gap:8, fontSize:'0.72rem', color:'var(--hp-dim)', padding:'3px 0 3px 12px', borderLeft:'2px solid var(--hp-border)', marginTop:3 }}>
                        <span style={{ flex:1, minWidth:0 }}>
                          {obDupeIds.has(e.id)
                            ? <span style={{ color:'#f59e0b', fontWeight:700 }}>possible duplicate {obTime(e.scannedAt)}</span>
                            : <>+ later {obTime(e.scannedAt)}</>}
                          {e.ocrError ? ' · read failed' : ''}
                        </span>
                        <span style={{ width:34, textAlign:'right', color: e.heldArticles > 0 ? '#6366f1' : 'var(--hp-dim)' }}>{e.heldArticles ?? '—'}</span>
                        <span style={{ width:44, textAlign:'right', color:'var(--hp-gold)', fontWeight:700 }}>{e.items ?? '—'}</span>
                        <span style={{ textAlign:'right', display:'inline-flex', gap:6, justifyContent:'flex-end', alignItems:'center' }}>
                          {e.ocrError && (
                            <button onClick={() => obReocr(e.id)} disabled={obBusyId === e.id} title="Re-read"
                              style={{ background:'none', border:'1px solid #f59e0b', borderRadius:5, color:'#f59e0b', fontSize:'0.64rem', fontWeight:700, padding:'1px 6px', cursor:'pointer' }}>
                              {obBusyId === e.id ? '…' : 'Re-read'}
                            </button>
                          )}
                          {e.viewUrl && (
                            <a href={e.viewUrl} target="_blank" rel="noreferrer" style={{ color:'var(--hp-dim)', display:'inline-flex' }} title="View the scanned photo">
                              <ImageIcon size={13} />
                            </a>
                          )}
                          {!isViewer && (
                            <button onClick={() => obRemove(e.id, s.driver)} disabled={obBusyId === e.id} title="Delete this capture"
                              style={{ background:'none', border:'none', color:'var(--hp-dim)', cursor:'pointer', padding:0, display:'inline-flex' }}>
                              <Trash2 size={12} />
                            </button>
                          )}
                        </span>
                      </div>
                    ))}
                    {(s.ocrError || s.checksumOk === false || (s.deviceLogin && s.deviceLogin !== s.driver)) && (
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:4, fontSize:'0.7rem', color:'#f59e0b' }}>
                        <AlertTriangle size={12} style={{ flexShrink:0 }} />
                        <span style={{ flex:1 }}>
                          {s.ocrError ? 'Photo stored, read failed' :
                            s.checksumOk === false ? "Lines don't add up to #Items — check the photo" :
                            `Device shows ${s.deviceLogin}`}
                        </span>
                        {s.ocrError && (
                          <button onClick={() => obReocr(s.id)} disabled={obBusyId === s.id}
                            style={{ background:'none', border:'1px solid #f59e0b', borderRadius:6, color:'#f59e0b', fontSize:'0.68rem', fontWeight:700, padding:'2px 8px', cursor:'pointer' }}>
                            {obBusyId === s.id ? '…' : 'Re-read'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Coverage reconciliation ── */}
      {tab === 'coverage' && (
        <div style={{ padding:'12px 16px', display:'flex', flexDirection:'column', gap:12 }}>

          {/* Scope + driver filter */}
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            <div style={{ display:'flex', gap:6 }}>
              {[['day','Day'],['week','Week'],['month','Month']].map(([k,lbl]) => (
                <button key={k} onClick={() => setCovScope(k)} style={{
                  flex:1, padding:'7px 0', borderRadius:'var(--radius)', cursor:'pointer',
                  fontWeight:700, fontSize:'0.72rem', letterSpacing:'0.05em', textTransform:'uppercase',
                  background: covScope === k ? 'var(--hp-surface)' : 'transparent',
                  color: covScope === k ? 'var(--hp-text)' : 'var(--hp-dim)',
                  border:`1px solid ${covScope === k ? 'var(--hp-gold)' : 'var(--hp-border)'}`,
                }}>{lbl}</button>
              ))}
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <input value={covDriver} onChange={e => setCovDriver(e.target.value)} placeholder="Filter driver login…"
                style={{ flex:1, padding:'7px 10px', borderRadius:'var(--radius)', border:'1px solid var(--hp-border)', background:'var(--hp-surface)', color:'var(--hp-text)', fontSize:'0.8rem', fontFamily:'var(--font-mono)' }} />
              {covDriver && <button onClick={() => setCovDriver('')} style={{ background:'none', border:'1px solid var(--hp-border)', borderRadius:'var(--radius)', color:'var(--hp-dim)', fontSize:'0.7rem', padding:'6px 10px', cursor:'pointer' }}>Clear</button>}
              {covScope !== 'day' && <span style={{ fontSize:'0.7rem', color:'var(--hp-dim)', whiteSpace:'nowrap' }}>{fmtRange(pvFrom, pvTo)}</span>}
            </div>
          </div>

          {/* ── DAY scope ── */}
          {covScope === 'day' && (loading || obLoading) && (
            <div style={{ textAlign:'center', padding:'40px 0', color:'var(--hp-dim)', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
              <RefreshCw size={16} style={{ animation:'spin 1s linear infinite' }} /> Loading…
            </div>
          )}

          {covScope === 'day' && !loading && !obLoading && visibleRows.length === 0 && (
            <div style={{ textAlign:'center', padding:'40px 0', color:'var(--hp-dim)', fontSize:'0.9rem' }}>
              No parcel data for this date yet.
            </div>
          )}

          {covScope === 'day' && !loading && !obLoading && visibleRows.length > 0 && (
            <>
              {/* Reconciliation waterfall — where the Day Total goes */}
              <div style={{ background:'var(--hp-card)', border:'1px solid var(--hp-border)', borderRadius:'var(--radius)', padding:'14px 16px' }}>
                <div style={{ fontSize:'0.7rem', color:'var(--hp-dim)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:10 }}>Where the day total goes</div>
                {[
                  { label:'Day Total (all signoffs)', value: covDayTotal, color:'var(--hp-gold)', strong:true },
                  { label:'less On Road', value: -covOnRoad, color:'#6366f1' },
                  { label:'less Express (late EP)', value: -covExpress, color:'#f59e0b' },
                  { label:'= Expected onboard', value: covTotals.expected, color:'var(--hp-text)', strong:true, rule:true },
                  { label:'Onboard captured (all batches)', value: covTotals.onboard, color:'var(--hp-text)' },
                  { label:'= Unaccounted (no image)', value: covResidual, color: covResidual > 0 ? '#ef4444' : '#34d399', strong:true, rule:true },
                ].map((r) => (
                  <div key={r.label} style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', padding:'5px 0', borderTop: r.rule ? '1px solid var(--hp-border)' : 'none', marginTop: r.rule ? 4 : 0 }}>
                    <span style={{ fontSize: r.strong ? '0.86rem' : '0.8rem', fontWeight: r.strong ? 800 : 500, color: r.strong ? 'var(--hp-text)' : 'var(--hp-dim)' }}>{r.label}</span>
                    <span style={{ fontSize: r.strong ? '1.05rem' : '0.9rem', fontWeight:800, color: r.color, fontVariantNumeric:'tabular-nums' }}>
                      {r.value < 0 ? `−${Math.abs(r.value).toLocaleString()}` : r.value.toLocaleString()}
                    </span>
                  </div>
                ))}
                <div style={{ fontSize:'0.72rem', color:'var(--hp-dim)', marginTop:10, lineHeight:1.5 }}>
                  On Road &amp; Express arrive on the device after the depot summary is photographed, so they can't be in an onboard image. The <strong style={{ color:'#ef4444' }}>unaccounted</strong> figure is depot load that should have an image but doesn't — the drivers below.
                </div>
              </div>

              {/* Who / why — the attention list */}
              {covFlagged.length === 0 ? (
                <div style={{ background:'rgba(52,211,153,0.08)', border:'1px solid rgba(52,211,153,0.4)', borderRadius:'var(--radius)', padding:'14px 16px', color:'#34d399', fontSize:'0.86rem', fontWeight:600 }}>
                  Every driver with a depot load has a matching onboard image. Nothing to chase.
                </div>
              ) : (
                <div style={{ background:'var(--hp-card)', border:'1px solid var(--hp-border)', borderRadius:'var(--radius)', padding:'12px 14px' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:8 }}>
                    <div style={{ fontWeight:800, fontSize:'0.9rem', color:'var(--hp-text)' }}>Missing / short images</div>
                    <div style={{ fontSize:'0.75rem', color:'var(--hp-dim)' }}>
                      {covFlagged.length} to chase · {covClean} clean
                    </div>
                  </div>

                  {/* Column headers */}
                  <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:'0.6rem', color:'var(--hp-dim)', textTransform:'uppercase', letterSpacing:'0.05em', padding:'2px 0 4px' }}>
                    <span style={{ flex:1 }}>Driver</span>
                    <span style={{ width:48, textAlign:'right' }}>Load</span>
                    <span style={{ width:48, textAlign:'right' }}>Image</span>
                    <span style={{ width:44, textAlign:'right' }}>Gap</span>
                  </div>

                  {covFlagged.map(r => {
                    const m = WHY_META[r.why];
                    return (
                      <div key={r.login} style={{ borderTop:'1px solid var(--hp-border)', padding:'8px 0' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:'0.82rem' }}>
                          <span style={{ flex:1, minWidth:0 }}>
                            <span style={{ fontFamily:'var(--font-mono)', fontWeight:700, color:'var(--hp-text)' }}>{r.login}</span>
                            <span style={{ color:'var(--hp-dim)', fontSize:'0.7rem' }}> {OB_AREA_LABEL[r.area] || r.area}</span>
                          </span>
                          <span style={{ width:48, textAlign:'right', color:'var(--hp-text)', fontWeight:700 }}>{r.expected.toLocaleString()}</span>
                          <span style={{ width:48, textAlign:'right', color: r.onboard > 0 ? 'var(--hp-gold)' : 'var(--hp-dim)', fontWeight:700 }}>{r.onboard || '—'}</span>
                          <span style={{ width:44, textAlign:'right', color:'#ef4444', fontWeight:800 }}>{r.gap > 0 ? r.gap.toLocaleString() : '—'}</span>
                        </div>
                        <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:4 }}>
                          <span style={{ fontSize:'0.62rem', fontWeight:800, textTransform:'uppercase', letterSpacing:'0.04em', color:m.color, background:`${m.color}1a`, border:`1px solid ${m.color}55`, borderRadius:5, padding:'1px 6px' }}>{m.label}</span>
                          <span style={{ flex:1, fontSize:'0.7rem', color:'var(--hp-dim)' }}>{m.note}</span>
                          {r.viewUrl && (
                            <a href={r.viewUrl} target="_blank" rel="noreferrer" style={{ color:'var(--hp-dim)', display:'inline-flex' }} title="View the scanned photo">
                              <ImageIcon size={13} />
                            </a>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* ── WEEK / MONTH scope ── */}
          {covScope !== 'day' && pvLoading && (
            <div style={{ textAlign:'center', padding:'40px 0', color:'var(--hp-dim)', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
              <RefreshCw size={16} style={{ animation:'spin 1s linear infinite' }} /> Loading…
            </div>
          )}

          {covScope !== 'day' && pvErr && (
            <div style={{ background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:'var(--radius)', padding:'12px 16px', color:'#ef4444', fontSize:'0.875rem' }}>
              {pvErr}
            </div>
          )}

          {covScope !== 'day' && !pvLoading && !pvErr && pvAll.length === 0 && (
            <div style={{ textAlign:'center', padding:'40px 0', color:'var(--hp-dim)', fontSize:'0.9rem' }}>
              No parcel data for this {covScope}.
            </div>
          )}

          {covScope !== 'day' && !pvLoading && !pvErr && pvAll.length > 0 && (
            <>
              {/* Period reconciliation waterfall */}
              <div style={{ background:'var(--hp-card)', border:'1px solid var(--hp-border)', borderRadius:'var(--radius)', padding:'14px 16px' }}>
                <div style={{ fontSize:'0.7rem', color:'var(--hp-dim)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:10 }}>Where the {covScope} total goes</div>
                {[
                  { label:'Day Total (all signoffs)', value: pvDayTotal, color:'var(--hp-gold)', strong:true },
                  { label:'less On Road', value: -pvTotals.onRoad, color:'#6366f1' },
                  { label:'less Express (late EP)', value: -pvTotals.express, color:'#f59e0b' },
                  { label:'= Expected onboard', value: pvTotals.expected, color:'var(--hp-text)', strong:true, rule:true },
                  { label:'Onboard captured (all batches)', value: pvTotals.onboard, color:'var(--hp-text)' },
                  { label:'= Unaccounted (no image)', value: pvResidual, color: pvResidual > 0 ? '#ef4444' : '#34d399', strong:true, rule:true },
                ].map((r) => (
                  <div key={r.label} style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', padding:'5px 0', borderTop: r.rule ? '1px solid var(--hp-border)' : 'none', marginTop: r.rule ? 4 : 0 }}>
                    <span style={{ fontSize: r.strong ? '0.86rem' : '0.8rem', fontWeight: r.strong ? 800 : 500, color: r.strong ? 'var(--hp-text)' : 'var(--hp-dim)' }}>{r.label}</span>
                    <span style={{ fontSize: r.strong ? '1.05rem' : '0.9rem', fontWeight:800, color: r.color, fontVariantNumeric:'tabular-nums' }}>
                      {r.value < 0 ? `−${Math.abs(r.value).toLocaleString()}` : r.value.toLocaleString()}
                    </span>
                  </div>
                ))}
                <div style={{ fontSize:'0.72rem', color:'var(--hp-dim)', marginTop:10, lineHeight:1.5 }}>
                  Summed across {fmtRange(pvFrom, pvTo)}. Onboard is matched per driver per day, so a driver who scans most days but skips one still shows that day's gap.
                </div>
              </div>

              {/* Repeat offenders */}
              {pvFlagged.length === 0 ? (
                <div style={{ background:'rgba(52,211,153,0.08)', border:'1px solid rgba(52,211,153,0.4)', borderRadius:'var(--radius)', padding:'14px 16px', color:'#34d399', fontSize:'0.86rem', fontWeight:600 }}>
                  No missed images this {covScope}. Every worked day has a matching scan.
                </div>
              ) : (
                <div style={{ background:'var(--hp-card)', border:'1px solid var(--hp-border)', borderRadius:'var(--radius)', padding:'12px 14px' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:8 }}>
                    <div style={{ fontWeight:800, fontSize:'0.9rem', color:'var(--hp-text)' }}>Repeat offenders</div>
                    <div style={{ fontSize:'0.75rem', color:'var(--hp-dim)' }}>
                      {pvFlagged.length} to chase · {pvClean} clean
                    </div>
                  </div>

                  {/* Column headers */}
                  <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:'0.6rem', color:'var(--hp-dim)', textTransform:'uppercase', letterSpacing:'0.05em', padding:'2px 0 4px' }}>
                    <span style={{ flex:1 }}>Driver</span>
                    <span style={{ width:52, textAlign:'right' }}>Missed</span>
                    <span style={{ width:52, textAlign:'right' }}>Load</span>
                    <span style={{ width:52, textAlign:'right' }}>Image</span>
                    <span style={{ width:48, textAlign:'right' }}>Gap</span>
                  </div>

                  {pvFlagged.map(a => (
                    <div key={a.login} style={{ borderTop:'1px solid var(--hp-border)', padding:'8px 0', display:'flex', alignItems:'center', gap:8, fontSize:'0.82rem' }}>
                      <span style={{ flex:1, minWidth:0 }}>
                        <span style={{ fontFamily:'var(--font-mono)', fontWeight:700, color:'var(--hp-text)' }}>{a.login}</span>
                        <span style={{ color:'var(--hp-dim)', fontSize:'0.7rem' }}> {OB_AREA_LABEL[a.area] || a.area}</span>
                      </span>
                      <span style={{ width:52, textAlign:'right', fontWeight:800, color: a.daysMissing > 0 ? '#ef4444' : 'var(--hp-dim)' }}>
                        {a.daysMissing}<span style={{ color:'var(--hp-dim)', fontWeight:500 }}>/{a.daysWorked}</span>
                      </span>
                      <span style={{ width:52, textAlign:'right', color:'var(--hp-text)', fontWeight:700 }}>{a.expected.toLocaleString()}</span>
                      <span style={{ width:52, textAlign:'right', color: a.onboard > 0 ? 'var(--hp-gold)' : 'var(--hp-dim)', fontWeight:700 }}>{a.onboard || '—'}</span>
                      <span style={{ width:48, textAlign:'right', color:'#ef4444', fontWeight:800 }}>{a.gap > 0 ? a.gap.toLocaleString() : '—'}</span>
                    </div>
                  ))}
                  <div style={{ fontSize:'0.66rem', color:'var(--hp-dim)', marginTop:8, lineHeight:1.5 }}>
                    <strong>Missed</strong> = worked days with no onboard image ÷ days worked. Sorted by missed days, then gap.
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Driver list ── */}
      {tab === 'totals' && (
      <div style={{ padding:'12px 16px', display:'flex', flexDirection:'column', gap:6 }}>

        {loading && (
          <div style={{ textAlign:'center', padding:'40px 0', color:'var(--hp-dim)', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
            <RefreshCw size={16} style={{ animation:'spin 1s linear infinite' }} /> Loading…
          </div>
        )}

        {error && (
          <div style={{ background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:'var(--radius)', padding:'12px 16px', color:'#ef4444', fontSize:'0.875rem' }}>
            {error}
          </div>
        )}

        {!loading && !error && visibleRows.length === 0 && (
          <div style={{ textAlign:'center', padding:'40px 0', color:'var(--hp-dim)', fontSize:'0.9rem' }}>
            No parcel data for this date yet.
          </div>
        )}

        {!loading && visibleRows.map((r, i) => (
          <div key={r.login} style={{ display:'flex', alignItems:'center', gap:12, background:'var(--hp-card)', border:'1px solid var(--hp-border)', borderRadius:'var(--radius)', padding:'12px 14px' }}>
            {/* Rank */}
            <div style={{ width:24, textAlign:'center', fontSize:'0.72rem', fontWeight:700, color:'var(--hp-dim)', flexShrink:0 }}>
              {i+1}
            </div>

            {/* Driver name */}
            <div style={{ flex:1, display:'flex', alignItems:'center', gap:8, fontFamily:'var(--font-mono)', fontWeight:700, fontSize:'0.85rem', color:'var(--hp-text)' }}>
              <DriverAvatar login={r.login} photo={photos[(r.login || '').toUpperCase()]} size={24} />
              {r.login}
            </div>

            {/* Parcel */}
            <div style={{ textAlign:'center', minWidth:48 }}>
              <div style={{ fontSize:'1.1rem', fontWeight:800, color:'var(--hp-text)', lineHeight:1 }}>{r.parcel}</div>
              <div style={{ fontSize:'0.6rem', color:'var(--hp-dim)', textTransform:'uppercase', letterSpacing:'0.06em', marginTop:1 }}>Prcl</div>
            </div>

            {/* Express */}
            <div style={{ textAlign:'center', minWidth:48 }}>
              <div style={{ fontSize:'1.1rem', fontWeight:800, color: r.express > 0 ? '#f59e0b' : 'var(--hp-dim)', lineHeight:1 }}>{r.express || '—'}</div>
              <div style={{ fontSize:'0.6rem', color:'var(--hp-dim)', textTransform:'uppercase', letterSpacing:'0.06em', marginTop:1 }}>Exp</div>
            </div>

            {/* On Road */}
            <div style={{ textAlign:'center', minWidth:48 }}>
              <div style={{ fontSize:'1.1rem', fontWeight:800, color: r.onRoad > 0 ? '#6366f1' : 'var(--hp-dim)', lineHeight:1 }}>{r.onRoad || '—'}</div>
              <div style={{ fontSize:'0.6rem', color:'var(--hp-dim)', textTransform:'uppercase', letterSpacing:'0.06em', marginTop:1 }}>O/Road</div>
            </div>

            {/* Total */}
            <div style={{ textAlign:'center', minWidth:52 }}>
              <div style={{ fontSize:'1.2rem', fontWeight:800, color:'var(--hp-gold)', lineHeight:1 }}>{r.total}</div>
              <div style={{ fontSize:'0.6rem', color:'var(--hp-dim)', textTransform:'uppercase', letterSpacing:'0.06em', marginTop:1 }}>Total</div>
            </div>
          </div>
        ))}
      </div>
      )}
    </div>
  );
}
